import { useContext } from "react";

import { useLazyQuery, useMutation } from "@apollo/client";
import { v4 as uuid } from 'uuid';

import { SAVE_NEW_RECIPE } from "../app/graphql/mutations";
import { GET_RECIPE_E3_FROM_ID } from "../app/graphql/queries";
import { Recipe, RecipeVariation, RecipeVariationMutation, RecipeVariationStep, RecipeVariationStepMutation, StepsMutation, StepSubstep, StepSubStepsMutation, SubstepIngredient, SubStepIngredientsMutation, SubstepsMutation } from "../app/recipeCreation/createRecipe.types";
import { useAppSelector } from "../app/store/hooks";
import { CREATED_SOURCE, RECIPE_CREATION_OPTIONS } from "../components/recipeCreation/utils/constants";
import { ErrorManagementContext } from "../utils/errorManagement/ErrorManagementProvider";
import useGenerateRecipe from "./useGenerateRecipe";
import useImageGeneration from "./useImageGeneration";

const recipeTransformer = ({ fetchedRecipe, recipeName, userId, userName, imageLink, isTemplate }: { fetchedRecipe: Recipe, recipeName: string, userId: string | null, userName: string | null, imageLink?: string | null, isTemplate: boolean }) => {
  const recipeVariations: RecipeVariationMutation[] = [];
  const recipeVariationSteps: RecipeVariationStepMutation[] = [];
  const steps: StepsMutation[] = [];
  const stepSubSteps: StepSubStepsMutation[] = [];
  const subSteps: SubstepsMutation[] = [];
  const subStepIngredients: SubStepIngredientsMutation[] = [];

  const { 'Recipe_Variations': fetchedRecipeVariations, __typename: recipeTypeName, ...recipeWithoutVariations } = fetchedRecipe;
  const recipeId = uuid();
  const currentDate = new Date();
  const currentFormattedTime = currentDate.toUTCString();
  const initializedBy = isTemplate ? RECIPE_CREATION_OPTIONS.TEMPLATE : RECIPE_CREATION_OPTIONS.DUPLICATE;

  // handling recipe
  const recipe = {
    ...recipeWithoutVariations,
    id: recipeId,
    created_by: userId,
    created_source: CREATED_SOURCE.USER_CREATED,
    created_at: currentFormattedTime,
    updated_at: currentFormattedTime,
    hidden_tags: [],
    is_public: false,
    editor: userId,
    last_edited_at: currentFormattedTime,
    name: recipeName,
    recipe_market_price: 0,
    author: userName,
    metadata: { initializedBy, sourceRecipe: fetchedRecipe.id }
  };

  fetchedRecipeVariations.forEach((recipeVariation: RecipeVariation) => {

    // handling recipeVariations
    const { RecipeVariations_Steps: fetchedRecipeVarSteps, __typename: recipeVarTypeName, ...recipeVarWithoutStepSubSteps } = recipeVariation;

    const recipeVariationId = uuid();
    recipeVariations.push({
      ...recipeVarWithoutStepSubSteps,
      id: recipeVariationId,
      created_at: currentFormattedTime,
      updated_at: currentFormattedTime,
      recipe_id: recipeId,
      image_url: imageLink ?? recipeVarWithoutStepSubSteps?.image_url,

    });

    // handling steps
    fetchedRecipeVarSteps.forEach((recipeVarStep: RecipeVariationStep) => {
      const { StepsE3: fetchedSteps, __typename: recipeVarStepTypeName, ...recipeVarStepWithoutSteps } = recipeVarStep;
      const recipeVarStepId = uuid();
      const stepId = uuid();

      recipeVariationSteps.push({
        ...recipeVarStepWithoutSteps,
        id: recipeVarStepId,
        recipeVariation_id: recipeVariationId,
        step_id: stepId,
      });

      const { Steps_Substeps: fetchedStepsSubsteps, __typename: stepsTypeName, ...stepWithoutStepSubSteps } = fetchedSteps;
      steps.push({
        ...stepWithoutStepSubSteps,
        id: stepId,
        created_at: currentFormattedTime,
        updated_at: currentFormattedTime,
      });

      fetchedStepsSubsteps.forEach((stepSubStep: StepSubstep) => {
        const { SubstepsE3: fetchedSubSteps, __typename: stepSubstepTypeName, ...stepSubStepWithoutSubSteps } = stepSubStep;
        const stepSubStepId = uuid();
        if (fetchedSubSteps.Recipe_Presets.length > 0) {

          stepSubSteps.push({
            ...stepSubStepWithoutSubSteps,
            id: stepSubStepId,
            step_id: stepId,
          });

        } else {

          const subStepId = uuid();

          stepSubSteps.push({
            ...stepSubStepWithoutSubSteps,
            id: stepSubStepId,
            step_id: stepId,
            subStep_id: subStepId,
          });

          const { SubSteps_Ingredients_E3s: fetchedSubStepIngredients, __typename: substepTypeName, Recipe_Presets: fetchedRecipePresets, ...subStepWithoutSubstepIngredientsRecipePresets } = fetchedSubSteps;

          subSteps.push({
            ...subStepWithoutSubstepIngredientsRecipePresets,
            id: subStepId,
            created_at: currentFormattedTime,
            updated_at: currentFormattedTime,
          });

          fetchedSubStepIngredients.forEach((subStepIngredient: SubstepIngredient) => {
            const subStepIngredientId = uuid();
            const { __typename: subStepIngredientTypeName, ...subStepIngredientWithoutTypeName } = subStepIngredient;
            subStepIngredients.push({
              ...subStepIngredientWithoutTypeName,
              id: subStepIngredientId,
              subStep_id: subStepId,
            });
          })
        }

      })

    })

  })

  return { recipeId, recipe, recipeVariations, steps, subSteps, recipeVariationSteps, stepSubSteps, subStepIngredients }
}

const useRecipeCreation = () => {
  const userId = useAppSelector(state => state.main.user?.id) ?? null;
  const userName = useAppSelector(state => state.main.user?.nickName) ?? null;
  const { cookClubLog } = useContext(ErrorManagementContext);
  const { generateImage, loading: generatingImage } = useImageGeneration();
  const { generateRecipe, loading: generatingRecipe, recipeId: recipeIdFromAIGeneration } = useGenerateRecipe();
  const [saveNewRecipe, { loading: savingRecipe }] = useMutation(SAVE_NEW_RECIPE);
  const [fetchRecipe, { loading: fetchingRecipe }] = useLazyQuery(GET_RECIPE_E3_FROM_ID);

  const duplicateRecipe = async (recipeId: string, recipeName: string, onCompleted: (recipeId: string) => void, onError?: (err: any) => void) => {
    try {
      const fetchedRecipe = await fetchRecipe({
        variables: {
          id: recipeId
        },
        onError: (err) => {
          cookClubLog(
            `Fetching recipeId ${recipeId}`,
            "ERROR_ON_ACCESS_RECIPE",
            { recipeId, err },
            { ui: { type: "error" }, browserConsole: true }
          )
        }
      }).catch((err) => {
        throw new Error('Error fetching recipe', err);
      });

      const { recipeId: newRecipeId, recipe, recipeVariations, steps, subSteps, recipeVariationSteps, stepSubSteps, subStepIngredients } = recipeTransformer({ fetchedRecipe: fetchedRecipe.data.Recipes_E3_by_pk, recipeName, userId, userName, isTemplate: false })
      await saveNewRecipe({
        variables: {
          recipe,
          recipeVariations,
          steps,
          subSteps,
          recipeVariationSteps,
          stepSubSteps,
          subStepIngredients
        },
        onError: (err) => {
          throw new Error('Error inserting recipe', err)
        }
      })
      onCompleted(newRecipeId);

    } catch (err) {
      if (onError) onError(err);
      console.error(err);
    }
  }

  const duplicateTemplateRecipe = async (recipeId: string, recipeName: string, onCompleted: (recipeId: string) => void, onError?: (err: any) => void) => {
    try {
      const fetchedRecipe = await fetchRecipe({
        variables: {
          id: recipeId
        },
        onError: (err) => {
          cookClubLog(
            `Fetching recipeId ${recipeId}`,
            "ERROR_ON_ACCESS_RECIPE",
            { recipeId, err },
            { ui: { type: "error" }, browserConsole: true }
          )
        }
      }).catch((err) => {
        throw new Error('Error fetching recipe', err)
      });

      const imageLink: string = await generateImage(recipeName);
      const { recipeId: newRecipeId, recipe, recipeVariations, steps, subSteps, recipeVariationSteps, stepSubSteps, subStepIngredients } = recipeTransformer({ fetchedRecipe: fetchedRecipe.data.Recipes_E3_by_pk, recipeName, userId, userName, imageLink, isTemplate: true })
      await saveNewRecipe({
        variables: {
          recipe,
          recipeVariations,
          steps,
          subSteps,
          recipeVariationSteps,
          stepSubSteps,
          subStepIngredients
        },
        onError: (err) => {
          if (onError) onError(err);
          throw new Error('Error inserting recipe', err)
        }
      })
      onCompleted(newRecipeId);

    } catch (err) {
      if (onError) onError(err);
      console.error(err);
    }
  }

  const generateAIRecipe = async (recipeName: string, onCompleted: (recipeId: string) => void, onError?: (err: any) => void) => {
    try {

      const recipeId = await generateRecipe(recipeName).catch((err) => {
        throw new Error('Error generating recipe', err)
      })
      if (recipeId) onCompleted(recipeId);

    } catch (err) {
      if (onError) onError(err);
      console.error(err)
    }
  }

  return { duplicateRecipe, duplicateTemplateRecipe, generateAIRecipe, loading: savingRecipe || fetchingRecipe || generatingImage || generatingRecipe };
}

export default useRecipeCreation;
